import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Chip,
  CircularProgress, Alert,InputAdornment,Paper, Pagination
} from '@mui/material';
import {
  Add, Edit, Delete, Preview, Save, Close, InfoOutlined, CheckCircle, Cancel
} from '@mui/icons-material';
import { api } from '../context/AuthContext';
import { FormControlLabel, Checkbox } from '@mui/material';

const TemplateManagement = ({ type }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'Assessment Scheduled',
    content: '',
    status: 'active',
    ai_enabled: true
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState('');
  const [manualEdit, setManualEdit] = useState(false);
  // Pagination
  const templatesPerPage = 4;
  const [page, setPage] = useState(1);

  const templateTypes = ['Assessment Scheduled', 'Technical Interview Scheduled', 'HR Interview Scheduled', 'Shortlisted', 'Selected', 'Rejected'];
  const variables = ['{{candidateName}}', '{{jobTitle}}', '{{companyName}}'];

  const autoContent = {
    'Assessment Scheduled': 'Hello {{candidateName}},\n\nYour assessment for {{jobTitle}} at {{companyName}} has been scheduled.\n\nPlease complete the assessment as per the provided instructions.\n\nThank you.',
    'Technical Interview Scheduled': 'Hello {{candidateName}},\n\nYour Technical Interview for {{jobTitle}} at {{companyName}} has been scheduled.\n\nPlease be available at the scheduled time.\n\nThank you.',
    'HR Interview Scheduled': 'Hello {{candidateName}},\n\nYour HR Interview for {{jobTitle}} at {{companyName}} has been scheduled.\n\nPlease be available at the scheduled time.\n\nThank you.',
    'Shortlisted': 'Hello {{candidateName}},\n\nCongratulations!\n\nYou have been shortlisted for the next stage of the hiring process for {{jobTitle}} at {{companyName}}.\n\nThank you.',
    'Selected': 'Hello {{candidateName}},\n\nCongratulations!\n\nYou have been selected for the position of {{jobTitle}} at {{companyName}}.\n\nOur team will contact you with the next steps.\n\nThank you.',
    'Rejected': 'Hello {{candidateName}},\n\nThank you for your interest in {{jobTitle}} at {{companyName}}.\n\nAfter careful consideration, we have decided not to move forward with your application at this time.\n\nWe wish you all the best for your future endeavors.'
  };

  useEffect(() => {
    fetchTemplates();
  }, [type]);

  const handleTypeChange = (newType) => {
    let updates = { template_type: newType };
    if (!manualEdit && autoContent[newType]) {
        updates.content = autoContent[newType];
    }
    setFormData({ ...formData, ...updates });
  };

  const handleContentChange = (content) => {
    setFormData({ ...formData, content });
    setManualEdit(true);
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/applications/comm-center/templates?type=${type}`);
      setTemplates(res.data);
      setPage(1);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        template_type: template.template_type,
        content: template.content,
        status: template.status,
        ai_enabled: template.ai_enabled ?? true
      });
      setManualEdit(true);
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        template_type: 'Assessment Scheduled',
        content: autoContent['Assessment Scheduled'],
        status: 'active',
        ai_enabled: true
      });
      setManualEdit(false);
    }
    setError('');
    setOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.content) {
        setError('Name and Content are required');
        return;
    }

    try {
      if (editingTemplate) {
        await api.put(`/applications/comm-center/templates/${editingTemplate.id}`, formData);
      } else {
        await api.post('/applications/comm-center/templates', { ...formData, type });
      }
      setOpen(false);
      fetchTemplates();
    } catch (err) {
        setError(err.response?.data?.message || 'Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await api.delete(`/applications/comm-center/templates/${id}`);
        fetchTemplates();
      } catch (err) {
        console.error('Failed to delete:', err);
      }
    }
  };

  const insertVariable = (variable) => {
    setFormData({ ...formData, content: formData.content + variable });
  };

  // =====================
// Pagination
// =====================
  const totalTemplates = templates.length;
  const totalPages = Math.ceil(totalTemplates / templatesPerPage);

  const startIndex = (page - 1) * templatesPerPage;
  const endIndex = startIndex + templatesPerPage;

  const currentTemplates = templates.slice(startIndex, endIndex);
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography
              variant="h4"
              sx={{
                color: "#1E3A8A",
                fontWeight: "bold",
                fontSize: {
                  xs: "1.5rem",
                  md: "2rem"
                }
              }}
              gutterBottom
          >
            {type === 'voice' ? 'Voice Call Templates' : 'WhatsApp Templates'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your automated {type === 'voice' ? 'voice call' : 'WhatsApp'} messages
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Create Template
        </Button>
      </Box>

      <Grid container spacing={3}>
        {currentTemplates.map((template) => (
          <Grid item xs={12} md={6} key={template.id}>
            <Card sx={{ height: '100%', borderRadius: '16px', position: 'relative' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700}>{template.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {type === 'voice' && (
                        <Chip 
                            label={template.ai_enabled ? "AI Enabled" : "AI Disabled"} 
                            size="small" 
                            icon={template.ai_enabled ? <CheckCircle /> : <Cancel />}
                            color={template.ai_enabled ? "success" : "default"}
                            variant="outlined"
                        />
                    )}
                    <Chip label={template.template_type} size="small" color="primary" variant="outlined" />
                  </Box>
                </Box>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        bgcolor: '#F8FAFC', 
                        p: 2, 
                        borderRadius: '8px', 
                        mb: 2, 
                        minHeight: 80,
                        whiteSpace: 'pre-wrap',
                        color: 'text.secondary',
                        fontFamily: 'monospace'
                    }}
                >
                  {template.content}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status: </Typography>
                    <Chip 
                        label={template.status} 
                        size="small" 
                        color={template.status === 'active' ? 'success' : 'default'} 
                        sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpen(template)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(template.id)} color="error"><Delete fontSize="small" /></IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {templates.length === 0 && (
            <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px', bgcolor: '#F8FAFC', border: '1px dashed #E2E8F0' }}>
                    <Typography color="text.secondary">No templates found. Create one to get started.</Typography>
                </Paper>
            </Grid>
        )}
      </Grid>

      {templates.length > 0 && (
          <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 5,
                width: "100%"
              }}
          >
            {/* Left Side */}
            <Typography
                sx={{
                  color: "#7C8DB5",
                  fontWeight: 500
                }}
            >
              Showing {startIndex + 1} to {Math.min(endIndex, totalTemplates)} of {totalTemplates} templates
            </Typography>

            {/* Right Side */}
            <Box sx={{ ml: "auto" }}>
              <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                  showFirstButton={false}
                  showLastButton={false}
                  siblingCount={1}
                  boundaryCount={1}
              />
            </Box>
          </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
                placeholder="e.g. Interview Invitation"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Template Type"
                value={formData.template_type}
                onChange={(e) => handleTypeChange(e.target.value)}
                sx={{ mb: 2 }}
              >
                {templateTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            {type === 'voice' && (
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={formData.ai_enabled} 
                                onChange={(e) => setFormData({ ...formData, ai_enabled: e.target.checked })} 
                            />
                        }
                        label="AI Calling Enabled"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, ml: 4 }}>
                        Only AI-enabled templates can be used for AI calling.
                    </Typography>
                </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ display: 'block', width: '100%', mb: 0.5 }}>Insert Variables:</Typography>
                {variables.map((v) => (
                  <Chip 
                    key={v} 
                    label={v} 
                    size="small" 
                    onClick={() => insertVariable(v)} 
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Template Content"
                value={formData.content}
                onChange={(e) => handleContentChange(e.target.value)}
                helperText={`${formData.content.length} characters`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button 
            variant="outlined" 
            startIcon={<Preview />} 
            onClick={() => setPreviewOpen(true)}
            disabled={!formData.content}
          >
            Preview
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Save Template</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent dividers>
            <Box sx={{ bgcolor: '#F8FAFC', p: 3, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {formData.content
                        .replace(/{{candidateName}}/g, 'John Doe')
                        .replace(/{{jobTitle}}/g, 'Software Engineer')
                        .replace(/{{companyName}}/g, 'ExiHire')
                    }
                </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                <InfoOutlined sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                This is a preview with sample data.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManagement;
